import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { useLanguage } from "@/contexts/LanguageContext";

type Experience = {
  company: string;
  role: string;
  role_bg: string;
  period: string;
  period_bg: string;
  description: string;
  description_bg: string;
  highlights: string[];
  highlights_bg: string[];
  tools: string[];
};

const experiences: Experience[] = [
  {
    company: "Kasha.io",
    role: "Head of Creative & UX/UI Design",
    role_bg: "Ръководител Creative & UX/UI Design",
    period: "Sep 2024 - Oct 2025",
    period_bg: "Сеп 2024 - Окт 2025",
    description:
      "As the Head of Creative & UX/UI Design at Kasha.io, I lead and mentor the creative and design teams, driving the development of user-centered digital experiences that align with strategic business goals. I established the company's entire brand identity and design system from the ground up, ensuring a consistent, scalable, and data-informed visual presence across all platforms.",
    description_bg:
      "Като ръководител на креативния и UX/UI екип в Kasha.io, водя и менторирам креативните и дизайн екипите, развивайки ориентирани към потребителя дигитални преживявания, които съответстват на стратегическите бизнес цели. Изградих цялата фирмена идентичност и дизайн система от нулата, осигурявайки последователно, скалируемо и базирано на данни визуално присъствие във всички платформи.",
    highlights: [
      "Integrated AI-driven design tools and processes (including generative design, automated asset creation, and data-informed user flow analysis) to accelerate creative production and improve design quality",
      "Leveraged AI-based analytics and behavioral insights to identify user pain points, optimize engagement, and personalize experiences in e-commerce and mobile interfaces",
      "Collaborated with AI and data teams to transform complex business data into intuitive, user-friendly dashboards and interactive experiences",
      "Framed product questions and reviewed workflows to guide design decisions and iteration",
      "Built and maintained a unified design system that incorporated adaptive, AI-assisted design components to ensure consistency and rapid iteration across products",
      "Mentored designers and creative technologists, promoting a culture of innovation through the exploration of new AI-driven design methodologies and user experience enhancements",
      "Partnered with founders and product leaders to connect business priorities with product and user needs",
    ],
    highlights_bg: [
      "Интегрирах AI-задвижвани дизайн инструменти и процеси (генеративен дизайн, автоматизирано създаване на ресурси и анализ на потребителските потоци на база данни), за да ускоря креативното производство и да подобря качеството на дизайна",
      "Използвах AI базирани анализи и поведенчески прозрения за откриване на потребителски затруднения, оптимизация на ангажираността и персонализация на изживяванията в електронната търговия и мобилните интерфейси",
      "Сътрудничех си с AI и data екипите, за да превърна сложни бизнес данни в интуитивни, удобни табла и интерактивни преживявания",
      "Рамкирах продуктови въпроси и преглеждах работни потоци, за да насочвам дизайн решенията и итерациите",
      "Изградих и поддържах единна дизайн система с адаптивни, AI-асистирани компоненти за последователност и бърза итерация между продуктите",
      "Менторирах дизайнери и креативни технолози, насърчавайки култура на иновации чрез изследване на нови AI-задвижвани дизайн методологии",
      "Работех с основателите и продуктовите лидери, за да свързвам бизнес приоритетите с продуктовите и потребителските нужди",
    ],
    tools: ["Figma", "User Experience Design (UX)", "User Interface Design (UI)", "Team Management", "User Research", "Artificial Intelligence"],
  },
  {
    company: "Otis AI",
    role: "Head of UX/UI & Business Analyst",
    role_bg: "Ръководител UX/UI и бизнес анализатор",
    period: "Sep 2023 - Sep 2025",
    period_bg: "Сеп 2023 - Сеп 2025",
    description:
      "In my role as the Head of UX/UI & Business Analyst at OtisAI, I lead the end-to-end design and user experience strategy for AI-driven marketing automation products. I'm responsible for crafting pixel-perfect, user-centric interfaces, managing project timelines, and analyzing user behavior to continuously enhance engagement and usability.",
    description_bg:
      "Като ръководител UX/UI и бизнес анализатор в OtisAI, водя пълната дизайн и UX стратегия за AI-задвижвани продукти за маркетинг автоматизация. Отговарям за изработката на pixel-perfect, ориентирани към потребителя интерфейси, управление на проектните срокове и анализ на потребителското поведение за постоянно подобрение на ангажираността и използваемостта.",
    highlights: [
      "Designed and optimized user experiences for AI-powered marketing tools, integrating intelligent recommendations and predictive analytics into the UX flow",
      "Collaborated closely with AI engineers and product managers to translate complex machine learning outputs into clear, human-centered design patterns",
      "Developed and refined UX flows across web and mobile product surfaces",
      "Led cross-functional teams, mentoring developers and designers to deliver scalable, intuitive solutions aligned with modern AI-driven product design standards",
      "Partnered with stakeholders to align business goals, product constraints and user needs",
      "Conducted comprehensive user research and usability testing, refining the interface based on behavioral data and feedback loops",
      "Worked with AI-focused design communities and platforms (including Lovable.dev and others) to prototype and test emerging AI-assisted design workflows",
    ],
    highlights_bg: [
      "Проектирах и оптимизирах потребителски преживявания за AI-задвижвани маркетинг инструменти, интегрирайки интелигентни препоръки и предсказателни анализи в UX потока",
      "Работех тясно с AI инженери и продуктови мениджъри, за да превърна сложни резултати на ML модели в ясни, ориентирани към човека дизайн модели",
      "Разработвах и прецизирах UX потоци за уеб и мобилни продуктови повърхности",
      "Водех мултифункционални екипи, менторирайки разработчици и дизайнери да доставят скалируеми, интуитивни решения по съвременни AI продуктови стандарти",
      "Работех със заинтересованите страни за съгласуване на бизнес цели, продуктови ограничения и потребителски нужди",
      "Провеждах задълбочено потребителско проучване и usability тестване, прецизирайки интерфейса на база поведенчески данни и feedback цикли",
      "Работех с AI-ориентирани дизайн общности и платформи (включително Lovable.dev и др.) за прототипиране и тестване на нови AI-асистирани дизайн работни процеси",
    ],
    tools: ["User Experience Design (UX)", "User Interface Development", "Data Analysis", "Product Manager", "Artificial Intelligence"],
  },
  {
    company: "OpenTag",
    role: "Senior UX/UI Designer",
    role_bg: "Старши UX/UI дизайнер",
    period: "Jun 2022 - Sep 2023",
    period_bg: "Юни 2022 - Сеп 2023",
    description:
      "At OpenTag as a Senior UX/UI Designer I'm responsible for dealing end to end with the clients by creating Pixel perfect Web/Native designs.",
    description_bg:
      "В OpenTag като старши UX/UI дизайнер отговарях изцяло за работата с клиентите чрез създаване на pixel-perfect уеб и native дизайни.",
    highlights: [
      "Create best possible User Experience NATIVE APP Designs",
      "Create creative new UI designs to make sure our users get best Experience while playing in our online casino & sportsbook",
      "Work close with Product owners and managers",
      "Mentor Junior UX/UI designers",
      "Ensure selling our Product to the clients through live meetings",
    ],
    highlights_bg: [
      "Създаване на най-добро потребителско изживяване за native приложения",
      "Създаване на креативни нови UI дизайни за нашия онлайн казино и sportsbook",
      "Тясна работа с product owners и мениджъри",
      "Менторство на junior UX/UI дизайнери",
      "Презентиране и продажба на продукта пред клиенти",
    ],
    tools: ["HTML", "Cascading Style Sheets (CSS)", "Figma", "Aligned"],
  },
  {
    company: "DXC Technology",
    role: "UX/UI Designer & Analyst",
    role_bg: "UX/UI дизайнер и анализатор",
    period: "Apr 2019 - Jun 2022",
    period_bg: "Апр 2019 - Юни 2022",
    description:
      "At DXC Technology as a UX/UI Designer I working in a scrum team to deliver best practices in the UX/UI world.",
    description_bg:
      "В DXC Technology като UX/UI дизайнер работех в scrum екип, за да доставям най-добрите практики в UX/UI света.",
    highlights: [
      "Create UX/UI design and work close with the developers on a platform for EUROPE CENTRAL BANK",
      "Internal UX solutions for different various projects",
      "Create New website design for VW GROUP SKODA",
      "Work close with AUDI HUNGARY to design and develop a better website for the clients",
    ],
    highlights_bg: [
      "Създаване на UX/UI дизайн и тясна работа с разработчиците по платформа за Европейската централна банка",
      "Вътрешни UX решения за различни проекти",
      "Нов уебсайт дизайн за VW GROUP SKODA",
      "Работа с AUDI HUNGARY за по-добър уебсайт",
    ],
    tools: ["Adobe XD", "Adobe InDesign", "Adobe Photoshop", "Adobe Illustrator", "Figma", "Webflow"],
  },
  {
    company: "Stellary Agency",
    role: "Web & UX/UI Designer & Front-end Developer",
    role_bg: "Уеб и UX/UI дизайнер & Front-end разработчик",
    period: "Jun 2018 - May 2019",
    period_bg: "Юни 2018 - Май 2019",
    description:
      "At Stellary Agency I was responsible for the UX/UI design and Front-end development of various designs.",
    description_bg:
      "В Stellary Agency отговарях за UX/UI дизайна и front-end разработката на различни проекти.",
    highlights: [
      "Design landing pages on adobe XD",
      "Developing landing pages with best responsivness and adaptation to the screen through HTML/CSS/JS",
      "UX solutions to different Apps",
      "Perfect end to end UI designs for different Local clients",
    ],
    highlights_bg: [
      "Дизайн на лендинг страници в Adobe XD",
      "Разработка на лендинг страници с най-добра адаптивност чрез HTML/CSS/JS",
      "UX решения за различни приложения",
      "Перфектни UI дизайни за местни клиенти",
    ],
    tools: ["HTML", "Cascading Style Sheets (CSS)", "JavaScript", "Adobe XD", "Adobe Photoshop", "Adobe Illustrator"],
  },
  {
    company: "Effective-tuning",
    role: "Senior UX/UI Researcher",
    role_bg: "Старши UX/UI изследовател",
    period: "Sep 2018 - Present",
    period_bg: "Сеп 2018 - до момента",
    description:
      "At Effective-Tuning as a Senior UX/UI Designer I'm responsible for dealing end to end with the interaction of the clients with the services that we offer by creating Pixel perfect user friendly and easy to understand checkout solutions.",
    description_bg:
      "В Effective-Tuning като старши UX/UI дизайнер отговарям за цялостното взаимодействие на клиентите с услугите ни чрез създаване на pixel-perfect, лесни за използване checkout решения.",
    highlights: [
      "Checkout solutions through the web portal",
      "Create best possible User Experience Website",
      "Create creative new solutions to help make checkout with a one click approach",
      "Work close with Product owners and managers to understand our clients and the business needs",
      "Mentor Junior UX/UI designers",
      "Review analytics and checkout behavior to identify opportunities for clearer customer journeys",
    ],
    highlights_bg: [
      "Checkout решения през уеб портала",
      "Създаване на най-добро потребителско изживяване",
      "Креативни решения за one-click checkout",
      "Тясна работа с product owners и мениджъри",
      "Менторство на junior UX/UI дизайнери",
      "Преглед на анализи и checkout поведение за по-ясни клиентски пътища",
    ],
    tools: ["Google Analytics", "Figma", "Bootstrap", "HTML", "Cascading Style Sheets (CSS)", "Laravel 5", "Bulma"],
  },
  {
    company: "Freelancer",
    role: "Front-end Developer & UX/UI Research",
    role_bg: "Front-end разработчик и UX/UI изследовател",
    period: "Feb 2017 - Feb 2018",
    period_bg: "Фев 2017 - Фев 2018",
    description: "Freelance work focusing on front-end development and UX/UI research for various clients.",
    description_bg:
      "Фрийланс работа с фокус върху front-end разработка и UX/UI проучване за различни клиенти.",
    highlights: [
      "Developed responsive web applications using modern frameworks",
      "Conducted user research and usability testing",
      "Created user-centered designs and prototypes",
      "Collaborated with clients to deliver tailored solutions",
    ],
    highlights_bg: [
      "Разработка на responsive уеб приложения с модерни рамки",
      "Провеждане на потребителско проучване и usability тестване",
      "Създаване на ориентирани към потребителя дизайни и прототипи",
      "Сътрудничество с клиенти за доставяне на индивидуални решения",
    ],
    tools: ["Python", "HTML", "Cascading Style Sheets (CSS)", "JavaScript", "React", "Figma"],
  },
  {
    company: "RadioActive",
    role: "Graphic Designer",
    role_bg: "Графичен дизайнер",
    period: "Sep 2016 - Sep 2017",
    period_bg: "Сеп 2016 - Сеп 2017",
    description:
      "At Radioactive as Junior Graphic designer I was responsible for creating unique banner designs for social media and Landing pages for the Forex market.",
    description_bg:
      "В Radioactive като junior графичен дизайнер отговарях за създаването на уникални банер дизайни за социални мрежи и лендинг страници за Forex пазара.",
    highlights: [
      "Provide eye catching designs to our clients",
      "Create responsive banners for different type of devices and sizes",
      "Create Landing pages with Mobile first approach",
    ],
    highlights_bg: [
      "Създаване на впечатляващи дизайни за клиентите",
      "Адаптивни банери за различни устройства и размери",
      "Лендинг страници с mobile-first подход",
    ],
    tools: ["Adobe Photoshop", "Adobe Illustrator"],
  },
];

export const Experience = () => {
  const { visibleItems, itemRefs } = useScrollAnimation();
  const { t, language } = useLanguage();

  const handleDownloadCV = () => {
    const link = document.createElement("a");
    link.href = "/CV_Khaled_Kasmou.pdf";
    link.download = "CV_Khaled_Kasmou.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section id="experience" className="py-24 px-6 relative overflow-hidden">
      {/* Artistic background elements */}
      <div className="absolute inset-0 -z-10 opacity-30">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-blob/40 to-transparent rounded-full blur-3xl animate-blob" />
        <div className="absolute top-1/3 right-20 w-96 h-96 bg-gradient-to-bl from-blob/30 to-transparent rounded-full blur-3xl animate-blob animation-delay-2000" />
        <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-gradient-to-tr from-blob/35 to-transparent rounded-full blur-3xl animate-blob animation-delay-4000" />
      </div>

      <div className="max-w-6xl mx-auto relative">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-center md:text-left mb-2 bg-gradient-to-r from-foreground via-blob to-foreground bg-clip-text text-transparent" data-gradient-text="true">
              {t("cv.section.title")}
            </h2>
            <div className="w-32 h-1 bg-gradient-to-r from-blob to-transparent mx-auto md:mx-0 rounded-full" />
          </div>
          <Button
            onClick={handleDownloadCV}
            className="mt-6 md:mt-0 gradient-primary text-primary-foreground hover:shadow-hover-strong transition-smooth group"
            size="lg"
          >
            <Download className="w-4 h-4 mr-2 group-hover:animate-bounce" />
            {t("cv.download")}
          </Button>
        </div>
        <p className="text-muted-foreground text-center md:text-left mb-16 text-lg">
          {t("cv.section.subtitle")}
        </p>

        <div className="space-y-8">
          {experiences.map((exp, index) => {
            const role = language === "bg" ? exp.role_bg : exp.role;
            const period = language === "bg" ? exp.period_bg : exp.period;
            const description = language === "bg" ? exp.description_bg : exp.description;
            const highlights = language === "bg" ? exp.highlights_bg : exp.highlights;
            return (
              <div
                key={index}
                ref={(el) => (itemRefs.current[index] = el)}
                className={`transition-all duration-700 ${
                  visibleItems.has(index)
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8"
                }`}
              >
                <Card className="p-6 md:p-8 shadow-card hover:shadow-hover-strong transition-smooth border-2 border-border/30 hover:border-blob/50 group relative overflow-hidden bg-card/50 backdrop-blur-sm">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-blob/10 to-transparent opacity-0 group-hover:opacity-100 transition-smooth" />
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-blob/10 to-transparent opacity-0 group-hover:opacity-100 transition-smooth" />

                  <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-4 relative">
                    <div>
                      <h3 className="text-2xl font-bold text-foreground group-hover:text-readable-accent transition-smooth">
                        {role}
                      </h3>
                      <p className="text-xl text-readable-accent font-medium flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-blob animate-pulse" />
                        {exp.company}
                      </p>
                    </div>
                    <span className="text-muted-foreground mt-2 md:mt-0 font-medium">{period}</span>
                  </div>

                  <p className="text-foreground mb-4 leading-relaxed">{description}</p>

                  <ul className="space-y-2 mb-6">
                    {highlights.map((highlight, hIndex) => (
                      <li key={hIndex} className="flex items-start text-muted-foreground group-hover:text-foreground transition-smooth">
                        <span className="text-readable-accent mr-2 mt-1 font-bold">→</span>
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="flex flex-wrap gap-2">
                    {exp.tools.map((tool, tIndex) => (
                      <Badge
                        key={tIndex}
                        variant="secondary"
                        className="bg-secondary/50 hover:bg-blob/10 hover:border-blob/50 hover:text-blob transition-smooth border border-transparent"
                      >
                        {tool}
                      </Badge>
                    ))}
                  </div>
                </Card>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
